import classNames from 'classnames'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { ActionIcon } from '@ui/components/ActionIcon/ActionIcon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { formatDate } from '@utils/date.utils'
import { getScrollToCardId } from '@utils/getItemId'

import { ClientCampaignRebateResultDto } from '@model/ClientCampaignRebateResultDto'
import {
  ClientRebateCampaignDetailDto,
  isCampaignCalculationAccount,
  isCampaignClientRebate,
} from '@model/ClientRebateCampaignDetailDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import styles from './CampaignRebateCard.module.scss'

interface CampaignRebateCardsProps extends Omit<CampaignRebateCardProps, 'item' | 'cardId'> {
  data: ClientCampaignRebateResultDto[]
}

export const CampaignRebateCards: React.FC<CampaignRebateCardsProps> = (props) => {
  const { data, campaign, campaignRebateValue, setInfoModal } = props
  const { t } = useTranslation()

  if (!data.length) {
    return <div className={classNames('text-align-center pt-4')}>{t('No results')}</div>
  }

  return data.map((x, index) => (
    <CampaignRebateCard
      key={`campaignRebateCard_${index}`}
      item={x}
      campaign={campaign}
      campaignRebateValue={campaignRebateValue}
      setInfoModal={setInfoModal}
      cardId={getScrollToCardId(index, data.length, 'campaign-total-rebate')}
    />
  ))
}

interface CampaignRebateCardProps {
  campaign: ClientRebateCampaignDetailDto
  item: ClientCampaignRebateResultDto
  campaignRebateValue: string
  setInfoModal(v: boolean): void
  cardId?: string
}

const CampaignRebateCard: React.FC<CampaignRebateCardProps> = (props) => {
  const { item, campaign, campaignRebateValue, setInfoModal, cardId } = props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const [auth] = useContext(AuthSessionContext)
  const { scrollIntoView } = useScrollToElementIds()

  const dateFormat = auth?.dateFormatType?.name
  const currencyId = campaign?.tradingAccount?.currencyId ?? item.currency.id

  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={styles.card}
      onClick={() => {
        if (!expanded && cardId) {
          scrollIntoView([cardId])
        }
        setExpanded((prev) => !prev)
      }}
      id={cardId}
    >
      <div className='is-flex is-justify-content-space-between'>
        <span className='has-text-weight-bold'>{t('Campaigns.Total Volume Traded')}</span>
        <span className='has-text-weight-bold'>
          {item.lots} {t('Campaigns.Lots')}
        </span>
      </div>

      <div className={styles.subtitle}>
        <span className='text-secondary'>{t('Campaigns.Round Date')}</span>
        <span>{formatDate(item.round, { formatType: dateFormat })}</span>
      </div>

      {expanded && (
        <>
          {hasRebateValueColumn(campaign) && (
            <div className={styles.subtitle}>
              <span className='text-secondary'>{campaignRebateValue}</span>
              <span>{item.rebateValue || '0'}</span>
            </div>
          )}

          <div className={styles.subtitle}>
            <span className='text-secondary'>{t('Campaigns.Accumulated Rebate')}</span>
            <span className='is-flex'>
              <span>{formatMoney(item.rebate, currencyId)}</span>
              {item.rebate > 0 && item.rebate < 1 && (
                <span className='ml-1 is-flex is-align-items-center'>
                  <ActionIcon icon={InfoIcon} size='x20' onClick={() => setInfoModal(true)} />
                </span>
              )}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

const hasRebateValueColumn = (detail?: ClientRebateCampaignDetailDto) => {
  return isCampaignClientRebate(detail) && !isCampaignCalculationAccount(detail)
}

export default CampaignRebateCard
